import { useState, useEffect } from "react";
import ObservationTable from "./components/observation-table";
import FilterBar from "./components/filter-bar";
import MainLayout from "../../components/layout/MainLayout";
import { Link } from "react-router-dom";
import {
  deleteObservation,
  getObservationByUserId,
} from "../../../Services/observationService";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { t } from "../../../utils/translate";

export default function AdminObservation() {
  const adminId = useSelector((state) => state?.auth?.loggedInUser?._id);
  const [observations, setObservations] = useState([]);
  const [filteredObservations, setFilteredObservations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [sortField, setSortField] = useState("updatedAt");
  const [sortDirection, setSortDirection] = useState("asc");
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [dateFilter, setDateFilter] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentObservation, setCurrentObservation] = useState(null);
  const [modalMode, setModalMode] = useState("view");

  useEffect(() => {
    if (!adminId) return;
    fetchObservations();
  }, [adminId]);

  const fetchObservations = async () => {
    try {
      const responseData = await getObservationByUserId(adminId);
      const observationsData = responseData?.data || [];
      const observationFilterData = observationsData?.filter(
        (data) => !data?.isDeleted
      );
      setObservations(observationFilterData);
    } catch (error) {
      console.error("Error fetching observations:", error);
      setIsLoading(false);
    } finally {
      setIsLoading(false);
    }
  };

  // Apply filters, sorting, and pagination
  useEffect(() => {
    let result = [...observations];

    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      result = result.filter(
        (obs) =>
          obs.projectDetail.observationTitle
            .toLowerCase()
            .includes(searchLower) ||
          obs.observerDetails.observerDetail
            .toLowerCase()
            .includes(searchLower) ||
          obs.projectNumber.toLowerCase().includes(searchLower) ||
          obs.observationDetails.observationDescription
            .toLowerCase()
            .includes(searchLower) ||
          obs.projectId.projectName.toLowerCase().includes(searchLower) ||
          obs.userId.name.toLowerCase().includes(searchLower)
      );
    }

    // Apply status filter
    if (statusFilter) {
      result = result.filter((obs) => obs.status === statusFilter);
    }

    // Apply date filter
    if (dateFilter) {
      const now = new Date();
      const filterDate = new Date(now);

      switch (dateFilter) {
        case "today":
          filterDate.setHours(0, 0, 0, 0);
          result = result.filter(
            (obs) => new Date(obs.updatedAt) >= filterDate
          );
          break;
        case "week":
          filterDate.setDate(now.getDate() - 7);
          result = result.filter(
            (obs) => new Date(obs.updatedAt) >= filterDate
          );
          break;
        case "month":
          filterDate.setMonth(now.getMonth() - 1);
          result = result.filter(
            (obs) => new Date(obs.updatedAt) >= filterDate
          );
          break;
        case "year":
          filterDate.setFullYear(now.getFullYear() - 1);
          result = result.filter(
            (obs) => new Date(obs.updatedAt) >= filterDate
          );
          break;
      }
    }

    // Apply sorting
    result.sort((a, b) => {
      let aValue, bValue;

      // Handle nested properties
      if (sortField.includes(".")) {
        const parts = sortField.split(".");
        aValue = parts.reduce((obj, key) => obj?.[key], a);
        bValue = parts.reduce((obj, key) => obj?.[key], b);
      } else {
        aValue = a[sortField];
        bValue = b[sortField];
      }

      // Handle dates
      if (
        sortField === "updatedAt" ||
        sortField === "createdAt" ||
        sortField === "projectDetail.dateOfObservation"
      ) {
        return sortDirection === "asc"
          ? new Date(aValue).getTime() - new Date(bValue).getTime()
          : new Date(bValue).getTime() - new Date(aValue).getTime();
      }

      // Handle strings
      if (typeof aValue === "string" && typeof bValue === "string") {
        return sortDirection === "asc"
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      // Handle numbers and other types
      return sortDirection === "asc"
        ? aValue > bValue
          ? 1
          : -1
        : bValue > aValue
        ? 1
        : -1;
    });

    setTotalItems(result.length);

    // Apply pagination
    const startIndex = (currentPage - 1) * itemsPerPage;
    const paginatedResult = result.slice(startIndex, startIndex + itemsPerPage);

    setFilteredObservations(paginatedResult);
  }, [
    observations,
    searchTerm,
    statusFilter,
    dateFilter,
    sortField,
    sortDirection,
    currentPage,
    itemsPerPage,
  ]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
    setCurrentPage(1); // Reset to first page on new search
  };

  const handleStatusFilter = (status) => {
    setStatusFilter(status);
    setCurrentPage(1);
  };

  const handleDateFilter = (date) => {
    setDateFilter(date);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (items) => {
    setItemsPerPage(items);
    setCurrentPage(1);
  };

  const handleViewObservation = (observation) => {
    setCurrentObservation(observation);
    setModalMode("view");
    setIsModalOpen(true);
  };

  const handleEditObservation = (observation) => {
    setCurrentObservation(observation);
    setModalMode("edit");
    setIsModalOpen(true);
  };

  const handleCreateObservation = () => {
    setCurrentObservation(null);
    setModalMode("create");
    setIsModalOpen(true);
  };

  const handleDeleteObservation = async (id) => {
    if (window.confirm("Are you sure you want to delete this observation?")) {
      try {
        // In a real app, this would be an API call
        // await fetch(`/api/observations/${id}`, { method: 'DELETE' });

        // Update local state
        const updatedObservations = observations.filter(
          (obs) => obs._id !== id
        );
        setObservations(updatedObservations);

        // Show success message
        alert("Observation deleted successfully");
      } catch (error) {
        console.error("Error deleting observation:", error);
        alert("Failed to delete observation");
      }
    }
  };

  const handleSaveObservation = (observation) => {
    if (modalMode === "create") {
      // In a real app, this would be an API call
      // const response = await fetch('/api/observations', {
      //   method: 'POST',
      //   body: JSON.stringify(observation),
      //   headers: { 'Content-Type': 'application/json' }
      // });

      // Simulate creating a new observation
      const newObservation = {
        ...observation,
        _id: `new-${Date.now()}`,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      setObservations([...observations, newObservation]);
    } else if (modalMode === "edit") {
      // In a real app, this would be an API call
      // const response = await fetch(`/api/observations/${observation._id}`, {
      //   method: 'PUT',
      //   body: JSON.stringify(observation),
      //   headers: { 'Content-Type': 'application/json' }
      // });

      // Update local state
      const updatedObservations = observations.map((obs) =>
        obs._id === observation._id
          ? { ...observation, updatedAt: new Date().toISOString() }
          : obs
      );

      setObservations(updatedObservations);
    }

    setIsModalOpen(false);
  };

  const handleDelete = async (id) => {
    const response = await deleteObservation(id);
    if (response.success === true) {
      fetchObservations();
      toast.success(t("deletedSuccessfully"));
    } else {
      toast.error(t("thereIsSomeError"));
    }
  };

  return (
    <MainLayout title={"Observation"}>
      <main className="min-h-screen bg-gray-50 p-4 md:p-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-between items-center mb-4">
            <div class="flex justify-center items-center">
              <h1 class="text-2xl md:text-3xl font-bold text-gray-800 text-center">
                Observation Data
              </h1>
            </div>

            <Link to={"/admin/observations/observation-create-page"}>
              <button className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors">
                Create New Observation
              </button>
            </Link>
          </div>

          <div className="bg-white rounded-lg shadow-md p-4 md:p-6 mb-6">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
              <FilterBar
                onSearch={handleSearch}
                onStatusFilter={handleStatusFilter}
                onDateFilter={handleDateFilter}
                searchTerm={searchTerm}
                statusFilter={statusFilter}
                dateFilter={dateFilter}
              />
            </div>

            <ObservationTable
              observations={filteredObservations}
              isLoading={isLoading}
              sortField={sortField}
              sortDirection={sortDirection}
              onSort={handleSort}
              onView={handleViewObservation}
              onEdit={handleEditObservation}
              onDelete={handleDeleteObservation}
              currentPage={currentPage}
              itemsPerPage={itemsPerPage}
              totalItems={totalItems}
              onPageChange={handlePageChange}
              onItemsPerPageChange={handleItemsPerPageChange}
              handleDelete={handleDelete}
            />
          </div>
        </div>
      </main>
    </MainLayout>
  );
}
